import i18n from '@/i18n';

/**
 * `Intl` formatting helpers reading `i18n.resolvedLanguage` at call time (SL-0027, Task 7.3), so
 * every call reflects whatever locale is active at that moment — no stale closure over a locale
 * captured at import time. Exactly these two exports exist because Task 9.4 names exactly five
 * call sites that need `formatDate`/`formatDateTime` and nothing else; a `formatNumber` or
 * `formatRelativeTime` export is added only by a future StepLedger that names a concrete call site
 * for it.
 */
export function formatDate(value: string | Date, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium', ...opts }).format(
    new Date(value),
  );
}

export function formatDateTime(value: string | Date, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...opts,
  }).format(new Date(value));
}

/**
 * Local-calendar "today" as `YYYY-MM-DD` (SL-0043, Resolution R5) — deliberately NOT
 * `new Date().toISOString().slice(0, 10)`, which reads the UTC calendar day and is off-by-one
 * near midnight in `it`/CET (UTC+1/+2). Reads the LOCAL calendar day the browser's own `Date`
 * getters report. The ONLY source of the required, non-editable `asOfDate` for the at-risk-list
 * and CRO-overview reads — never a date picker, per the tracked BK-003 follow-up
 * (`platform/clinical-study/docs/project-context.md`) this closes on the UI side.
 */
export function todayLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
